<script>
import { useMailV2Store } from '../../stores/mailV2'
import MailV2TemplateList from './MailV2TemplateList.vue'
import MailV2Editor from './MailV2Editor.vue'
import MailV2Settings from './MailV2Settings.vue'

function getTranslatable(val, locale) {
  if (!val) return ''
  if (typeof val === 'string') return val
  return val[locale] || val.fr || val.en || Object.values(val).find(function (v) { return v }) || ''
}

export default {
  name: 'MailV2TemplateManager',
  components: {
    MailV2TemplateList: MailV2TemplateList,
    MailV2Editor: MailV2Editor,
    MailV2Settings: MailV2Settings,
  },
  props: {
    holderType: { type: String, required: true },
    holderId: { type: [String, Number], required: true },
    locale: { type: String, default: '' },
  },
  data: function () {
    return {
      store: useMailV2Store(),
      activeTab: 'templates',
      isEditorOpen: false,
      editingItem: null,
      editingTemplate: null,
    }
  },
  computed: {
    drawerTitle: function () {
      var title = this.$ecTranslate('MV2.EDIT_TEMPLATE')
      if (this.editingItem) {
        title += ' — ' + getTranslatable(this.editingItem.type_name, this.locale || 'fr')
      }
      return title
    },
  },
  methods: {
    async onCustomize(item) {
      var confirmed = await this.$ecConfirm(
        this.$ecTranslate('MV2.CUSTOMIZE_CONFIRM').replace('{name}', getTranslatable(item.type_name, this.locale || 'fr')),
        { confirmButtonText: this.$ecTranslate('MV2.CUSTOMIZE'), icon: 'question' },
      )
      if (!confirmed) return

      try {
        var template = await this.store.customizeTemplate(this.holderType, this.holderId, item.mail_type_id != null ? item.mail_type_id : item.id)
        this.editingItem = item
        this.editingTemplate = template
        this.isEditorOpen = true
        this.$ecNotify(this.$ecTranslate('MV2.TEMPLATE_CUSTOMIZED'), 'success')
        this.store.listTemplates(this.holderType, this.holderId)
      } catch (e) {
        this.$ecNotify(this.$ecTranslate('MV2.CUSTOMIZE_ERROR'), 'error')
      }
    },
    onEdit: function (item) {
      this.editingItem = item
      this.editingTemplate = item.template || null
      this.isEditorOpen = true
    },
    async onReset(item) {
      var confirmed = await this.$ecConfirm(this.$ecTranslate('MV2.RESET_CONFIRM'), {
        confirmButtonText: this.$ecTranslate('MV2.RESET_DEFAULT'),
        icon: 'warning',
      })
      if (!confirmed) return

      try {
        await this.store.deleteTemplate(this.holderType, this.holderId, item.template.id)
        this.$ecNotify(this.$ecTranslate('MV2.TEMPLATE_RESET'), 'success')
        this.store.listTemplates(this.holderType, this.holderId)
      } catch (e) {
        this.$ecNotify(this.$ecTranslate('MV2.RESET_ERROR'), 'error')
      }
    },
    onEditorSaved: function () {
      this.closeEditor()
      this.store.listTemplates(this.holderType, this.holderId)
    },
    closeEditor: function () {
      this.isEditorOpen = false
      this.editingItem = null
      this.editingTemplate = null
    },
  },
}
</script>

<template>
  <div class="ec-mv2-manager">
    <!-- Tabs -->
    <div class="ec-mv2-tabs">
      <button
        class="ec-mv2-tab"
        :class="{ 'ec-mv2-tab--active': activeTab === 'templates' }"
        @click="activeTab = 'templates'"
      >
        <i class="bi bi-envelope-open" />
        {{ $ecTranslate('MV2.TEMPLATES_LIST') }}
      </button>
      <button
        class="ec-mv2-tab"
        :class="{ 'ec-mv2-tab--active': activeTab === 'settings' }"
        @click="activeTab = 'settings'"
      >
        <i class="bi bi-gear" />
        {{ $ecTranslate('MV2.MAIL_SETTINGS') }}
      </button>
    </div>

    <!-- Tab content -->
    <div class="ec-mv2-tab-content">
      <template v-if="activeTab === 'templates'">
        <MailV2TemplateList
          :holder-type="holderType"
          :holder-id="holderId"
          :locale="locale"
          @customize="onCustomize"
          @edit="onEdit"
          @reset="onReset"
        />
      </template>

      <template v-if="activeTab === 'settings'">
        <MailV2Settings :holder-type="holderType" :holder-id="holderId" />
      </template>
    </div>

    <!-- Editor drawer -->
    <EcDrawer
      :value="isEditorOpen"
      :title="drawerTitle"
      :resizable="true"
      :fullscreen="true"
      @input="function (v) { if (!v) closeEditor() }"
    >
      <MailV2Editor
        v-if="editingItem && editingTemplate"
        :template-data="editingTemplate"
        :mail-type="editingItem"
        :holder-type="holderType"
        :holder-id="holderId"
        :locale="locale"
        @saved="onEditorSaved"
        @cancel="closeEditor"
      />
    </EcDrawer>
  </div>
</template>

<style scoped>
.ec-mv2-manager {
  width: 100%;
}
.ec-mv2-tabs {
  display: flex;
  gap: 0;
  border-bottom: 2px solid var(--ec-border-color, #e9ecef);
  margin-bottom: 16px;
}
.ec-mv2-tab {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  font-size: 14px;
  font-weight: 500;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #666;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  transition: all 0.15s;
}
.ec-mv2-tab--active {
  color: var(--ec-primary-color, #0091ca);
  border-bottom-color: var(--ec-primary-color, #0091ca);
}
.ec-mv2-tab-content {
  padding: 4px 0;
}
</style>
